/**
 * MA-ARCH-201 Phase 3: Permission Hook
 * 
 * Fetches user's effective permissions for EXPLICIT account context.
 * Backend access context requires account_id - NO implicit inference.
 * 
 * CRITICAL: Does NOT use user.role or user.tenant_id for any logic.
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useTenant } from '@/contexts/TenantContext';

/**
 * Fetch user's access context from backend.
 * 
 * MA-ARCH-201 Phase 3: Requires EXPLICIT account_id parameter.
 * 
 * @param {string|null} accountId - Account ID for permission resolution (REQUIRED for non-SuperAdmin)
 */
async function fetchAccessContext(accountId) {
  // MA-ARCH-201 Phase 3: Pass explicit account context to backend
  const params = accountId ? { account_id: accountId } : {};
  const response = await api.get('/auth/me/access-context', { params });
  return response.data;
}

/**
 * Hook to access user's effective permissions.
 * 
 * MA-ARCH-201 Phase 3: Uses EXPLICIT account context from TenantContext.
 * MA-SESSION-CACHE-ISOLATION-117B: Forces fresh refetch on mount to prevent
 * User B from inheriting User A's cached permissions after logout/login.
 * 
 * Returns:
 * - effective_modules: Array of module IDs user can access
 * - effective_settings_sections: Array of settings section IDs user can access
 * - is_super_admin: Boolean indicating SuperAdmin status
 * - user_role: User's membership role in current account
 * - account info, billing status, etc.
 * 
 * CRITICAL: Permission resolution is scoped to explicit account_id.
 * No implicit account selection happens on backend.
 */
export function usePermissions() {
  // MA-ARCH-201 Phase 3: Get explicit account context from TenantContext
  const { selectedTenantId } = useTenant();
  
  const { data, isLoading, error } = useQuery({
    // MA-ARCH-201 Phase 3: Query key includes account_id for proper cache isolation
    queryKey: ['permissions', 'access-context', selectedTenantId],
    queryFn: () => fetchAccessContext(selectedTenantId),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 2,
    // Don't fetch if no account context available yet
    enabled: !!selectedTenantId,
    // MA-SESSION-CACHE-ISOLATION-117B: Force refetch on mount to ensure fresh permissions
    // This prevents stale cache hits after logout/login, ensuring User B never sees User A's permissions
    refetchOnMount: "always",
  });

  // P4-WP-9A.2a: the authorization-resolution invariant.
  //
  // `isLoading` CANNOT answer "is authorization resolved?". React Query v5
  // defines `isLoading = isPending && isFetching` (query-core queryObserver),
  // and this query is `enabled: !!selectedTenantId`. A DISABLED query never
  // enters `fetchStatus: "fetching"`, so on first login -- before
  // TenantContext has resolved an Account -- the hook reports:
  //
  //     status = "pending", fetchStatus = "idle"
  //       => isLoading === false, data === undefined
  //
  // That is "never resolved", not "resolved", and it is the state WP-9A.2's
  // `permissionsLoading` guard could not distinguish.
  //
  // The predicate is ACCOUNT-SCOPED rather than merely `!!data`: a response
  // only describes authorization for the Account it was fetched for. Requiring
  // `data.account_id === selectedTenantId` also closes the window where data
  // for a previous Account is still in hand while a new Account's query
  // resolves -- during an Account switch, the old answer must not be treated
  // as authority for the new Account.
  //
  // Consumers that gate authorization-derived rendering should use THIS, not
  // `isLoading`. It is deliberately additive: every existing field keeps its
  // current meaning.
  const isResolvedForSelectedAccount =
    !!data && !!selectedTenantId && data.account_id === selectedTenantId;

  // MA-PHASE11A DEBUG: Log permissions
  const result = {
    // Permission data
    effectiveModules: data?.effective_modules || [],
    effectiveSettings: data?.effective_settings_sections || [],
    
    // MA-RBAC-BATCH3-INFRA-115A: Module access levels for read/full separation
    moduleAccessLevels: data?.module_access_levels || {},
    
    // User info
    isSuperAdmin: data?.is_super_admin || false,
    userRole: data?.user_role,
    membershipStatus: data?.membership_status,
    hasActiveMembership: data?.has_active_membership !== false,
    
    // Account info
    accountId: data?.account_id,
    accountName: data?.account_name,
    accountPlan: data?.account_plan,
    accountType: data?.account_type,
    parentAccountId: data?.parent_account_id,
    
    // Account management capabilities (MA-PHASE11A)
    canManage: data?.can_manage || false,
    canCreateSubaccounts: data?.can_create_subaccounts || false,
    
    // Account status
    executionSetupStatus: data?.execution_setup_status,
    onboardingStatus: data?.onboarding_status,
    billingStatus: data?.billing_status,
    billingActivationRequired: data?.billing_activation_required,
    
    // Loading state
    isLoading,
    error,

    // P4-WP-9A.2a: true only when the permissions response in hand belongs to
    // the currently selected Account. See the derivation above.
    isResolvedForSelectedAccount,
    
    // Full context (if needed)
    fullContext: data,
  };
  
  // MA-PHASE11A-FINAL-CONSOLE-CLEANUP-023: Debug logging removed for production
  // Production-ready hook with no console noise
  
  return result;
}

/**
 * Check if user has access to a specific module.
 * 
 * MA-ARCH-201 Phase 3: Module visibility enforcement.
 */
export function useHasModule(moduleId) {
  const { effectiveModules, isLoading } = usePermissions();
  
  return {
    hasAccess: effectiveModules.includes(moduleId),
    isLoading,
  };
}

/**
 * Check module access level (off/read/view/write/full) for RBAC enforcement.
 * 
 * MA-RBAC-BATCH3-INFRA-115A: Enables frontend to distinguish between
 * profit_paths=read and profit_paths=full for builder access control.
 * 
 * Access levels:
 * - "off": No access (module not available)
 * - "read" or "view": Read-only access (can view, cannot mutate)
 * - "write": Can create/edit but not full admin
 * - "full": Full access including builder/admin features
 * 
 * @param {string} moduleId - Module to check (e.g., "profit_paths", "landing_pages")
 * @returns {Object} Access level information
 * 
 * @example
 * const { hasFullAccess } = useModuleAccessLevel("profit_paths");
 * {hasFullAccess && <Button>+ New Profit Path</Button>}
 */
export function useModuleAccessLevel(moduleId) {
  const { moduleAccessLevels, isLoading } = usePermissions();
  
  const accessLevel = moduleAccessLevels[moduleId] || "off";
  
  // Determine read and full access based on access level
  const hasReadAccess = ["read", "view", "write", "full"].includes(accessLevel);
  const hasFullAccess = accessLevel === "full";
  
  return {
    accessLevel,      // "off", "read", "view", "write", "full"
    hasReadAccess,    // true for read/view/write/full, false for off
    hasFullAccess,    // true only for full, false otherwise
    isLoading,
  };
}

/**
 * Check if user has access to a specific settings section.
 * 
 * MA-ARCH-201 Phase 3: Settings visibility enforcement.
 */
export function useHasSettingsSection(sectionId) {
  const { effectiveSettings, isLoading } = usePermissions();
  
  return {
    hasAccess: effectiveSettings.includes(sectionId),
    isLoading,
  };
}

/*
 * P4-WP-3: `useModuleNavigation` and `useSettingsNavigation` were removed here.
 *
 * Both carried hardcoded route maps built on pre-P4 module ids (`dashboard`,
 * `script_builder`, `message_assist`, `knowledge_base`, `analytics`,
 * `funnel_analytics`, `team`) that no longer exist in the canonical 17-module
 * registry, so they could not have produced correct navigation. A repository
 * scan proved zero consumers before removal, and
 * `usePermissions.deadNavigationHooks.test.js` fails if either name returns.
 *
 * navigationConfig.js is the canonical navigation declaration.
 */

/**
 * Check if user has access to ANY of multiple modules.
 * 
 * MA-PHASE10: Convenience hook for OR-based module checks.
 * 
 * @param {string[]} moduleIds - Array of module IDs to check
 * @returns {{ hasAccess: boolean, isLoading: boolean }}
 */
export function useHasAnyModule(moduleIds) {
  const { effectiveModules, isLoading } = usePermissions();
  
  return {
    hasAccess: moduleIds.some(id => effectiveModules.includes(id)),
    isLoading,
  };
}

/**
 * Check if current user is a SuperAdmin.
 * 
 * MA-PHASE10: Convenience hook for SuperAdmin detection.
 * SuperAdmins have full access to all modules and settings.
 * 
 * @returns {{ isSuperAdmin: boolean, isLoading: boolean }}
 */
export function useIsSuperAdmin() {
  const { isSuperAdmin, isLoading } = usePermissions();
  
  return {
    isSuperAdmin,
    isLoading,
  };
}

/**
 * Alias for useHasModule - provided for API consistency.
 * 
 * MA-PHASE10: Alternative naming convention.
 * 
 * @param {string} moduleId - Module ID to check
 * @returns {{ hasAccess: boolean, isLoading: boolean }}
 */
export function useCanAccessModule(moduleId) {
  return useHasModule(moduleId);
}

/**
 * Alias for useHasSettingsSection - provided for API consistency.
 * 
 * MA-PHASE10: Alternative naming convention.
 * 
 * @param {string} sectionId - Settings section ID to check
 * @returns {{ hasAccess: boolean, isLoading: boolean }}
 */
export function useCanAccessSettings(sectionId) {
  return useHasSettingsSection(sectionId);
}